import { useEffect, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DateTime as DT } from 'luxon';
import type { DocumentDirectoryType, LeadCloudFileType, ObjectDocumentAiDocumentType } from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import * as apiDocument from 'api/apiDocument';
import * as apiObject from 'api/apiObjects';
import { getCloudflareUploadUrls } from 'api/apiCloudflare';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { getProposalUseUsd } from 'modules/templates';
import { safeFloatParse } from 'utils/helpers/safeParsing';
import { useObjectCard } from '../context/ObjectCardProvider';
import UploadDocumentForm, { type UploadDocumentFormValues } from './UploadDocumentForm';
import UploadDocumentAiPanel from './UploadDocumentAiPanel';
import useUploadDocumentAiInfo from './model/useUploadDocumentAiInfo';
import { mergeDocumentFromAi } from './model/documentAiUtils';

export interface OffUploadDocumentProps {
    show: boolean;
    setShow: (show: boolean) => void;
    objectId: number;
    onUploaded?: () => Promise<void> | void;
}

const createEmptyValues = (usdDefault = false, vatDefault = false): UploadDocumentFormValues => ({
    proposalType: 'invoice',
    proposalName: '',
    proposalNumber: '',
    proposalDt: DT.now().startOf('day').toMillis(),
    generationComment: '',
    proposalAmount: '',
    markPaid: false,
    pricesInUsd: Boolean(usdDefault),
    proposalVat: Boolean(vatDefault),
});

const OffUploadDocument = ({ show, setShow, objectId, onUploaded }: OffUploadDocumentProps) => {
    const { t } = useTranslation(['Objects', 'Document', 'common']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const { fetchObject } = useObjectCard();
    const rate = Number(localSettings?.RATE?.current || 1) || 1;
    const canMarkPaid = Boolean(params.objectPaymentControl);
    const [activeTab, setActiveTab] = useState<'form' | 'ai'>('form');
    const [formValues, setFormValues] = useState<UploadDocumentFormValues>(() =>
        createEmptyValues(params.usdDefault, params.vatDefault),
    );
    const [files, setFiles] = useState<File[]>([]);
    const [cloudFile, setCloudFile] = useState<LeadCloudFileType | null>(null);
    const [uploadProgress, setUploadProgress] = useState<number | boolean>(false);
    const [loading, setLoading] = useState(false);

    const resetState = () => {
        setActiveTab('form');
        setFormValues(createEmptyValues(params.usdDefault, params.vatDefault));
        setFiles([]);
        setCloudFile(null);
        setUploadProgress(false);
        setLoading(false);
    };

    useEffect(() => {
        if (show) {
            setFormValues(createEmptyValues(params.usdDefault, params.vatDefault));
        }
    }, [show, params.usdDefault, params.vatDefault]);

    const handleClose = () => {
        setShow(false);
        setTimeout(() => {
            resetState();
        }, 300);
    };

    const handleAiParsed = (
        parsed: ObjectDocumentAiDocumentType,
        parsedCloudFile: LeadCloudFileType,
        file: File,
    ) => {
        setFormValues((prev) => mergeDocumentFromAi(prev, parsed, { allowMarkPaid: canMarkPaid }));
        setCloudFile(parsedCloudFile);
        setFiles([file]);
        setActiveTab('form');
    };

    const {
        refInputFile,
        isBusy: aiBusy,
        uploadProgress: aiUploadProgress,
        phase: aiPhase,
        processingLabel,
        handleOpenClick,
        handleChangeFile,
        handleCancel,
    } = useUploadDocumentAiInfo({
        objectId,
        onParsed: handleAiParsed,
    });

    const uploadFileToCloudflare = async (
        targetObjectId: number,
        file: File,
    ): Promise<LeadCloudFileType | null> => {
        const uploadConfigs = await getCloudflareUploadUrls(
            [
                {
                    fileName: file.name,
                    contentType: file.type || 'application/octet-stream',
                    fileSize: file.size,
                },
            ],
            { objectId: targetObjectId },
        );
        if (!uploadConfigs?.length) return null;

        setUploadProgress(0);
        const uploaded = await uploadFilesWithOverallProgress([file], uploadConfigs, (percent) => {
            setUploadProgress(percent);
        });
        const uploadedFile = uploaded?.[0];
        if (!uploadedFile?.success) return null;
        return uploadedFile.config;
    };

    const handleSubmit = async (values: UploadDocumentFormValues) => {
        const targetObjectId = Number(objectId || 0);
        if (!targetObjectId) {
            showToast(t('messSaveObjectFirst'), 'warning');
            return;
        }
        const proposalName = String(values.proposalName || '').trim();
        if (!proposalName) {
            showToast(t('Document:edit.fields.proposalNameRequired'), 'warning');
            return;
        }

        const proposalAmount = safeFloatParse(values.proposalAmount || 0);
        const markPaid = canMarkPaid && values.markPaid;
        if (markPaid && !(proposalAmount > 0)) {
            showToast(t('documents.upload.markPaidAmountRequired'), 'warning');
            return;
        }

        setLoading(true);
        try {
            let fileToSave: LeadCloudFileType | null = cloudFile;
            if (!fileToSave && files.length) {
                fileToSave = await uploadFileToCloudflare(targetObjectId, files[0]);
                if (!fileToSave) {
                    showToast(t('common:messError'), 'danger');
                    return;
                }
            }

            const pricesInUsd = Boolean(values.pricesInUsd);
            const created = await apiDocument.createObjectDocumentGeneration(targetObjectId, {
                dirProposalId: 0,
                proposalType: values.proposalType as DocumentDirectoryType,
                proposalName,
                proposalNumber: String(values.proposalNumber || '').trim() || null,
                proposalDt: values.proposalDt && values.proposalDt > 0 ? values.proposalDt : null,
                file: fileToSave,
                sentToEmail: false,
                sentEmail: '',
                generationComment: String(values.generationComment || '').trim().slice(0, 500),
                proposalAmount,
                proposalUsd: getProposalUseUsd(pricesInUsd, rate),
                proposalVat: Boolean(values.proposalVat),
                products: [],
                additionalItems: [],
                pricesInUsd,
            });

            if (!created?.stat || !created.generationId) {
                showToast(t('common:messError'), 'danger');
                return;
            }

            if (markPaid && proposalAmount > 0) {
                const payment = await apiDocument.addObjectDocumentPayment(
                    targetObjectId,
                    created.generationId,
                    {
                        paymentAmount: proposalAmount,
                        description: t('documents.upload.markPaidPaymentNote'),
                        attach: [],
                    },
                );
                if (!payment?.stat) {
                    showToast(t('documents.upload.markPaidPaymentError'), 'warning');
                } else if (params.autoSyncPayStatus) {
                    await apiObject.syncPayStatusIfCovered(targetObjectId);
                }
            }

            showToast(t('documents.upload.messSuccess'), 'success');
            if (fetchObject) await fetchObject();
            if (onUploaded) await onUploaded();
            handleClose();
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
        } finally {
            setLoading(false);
            setUploadProgress(false);
        }
    };

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{t('documents.upload.title')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                {show ? (
                    <div className="d-flex flex-column h-100">
                        <div className="px-3 pt-2 mb-3 pb-1">
                            <div className="btn-group w-100">
                                <Button
                                    size="sm"
                                    variant={activeTab === 'form' ? 'primary' : 'outline-primary'}
                                    onClick={() => setActiveTab('form')}
                                    disabled={aiBusy}
                                >
                                    <i className="bi bi-pencil-square me-1"></i>
                                    {t('documents.upload.tabForm')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant={activeTab === 'ai' ? 'light' : 'outline-secondary'}
                                    className={activeTab === 'ai' ? 'off-coming-ai-tab is-active' : 'off-coming-ai-tab'}
                                    onClick={() => setActiveTab('ai')}
                                    disabled={loading || uploadProgress !== false || aiBusy}
                                >
                                    <i className="bi bi-stars me-1"></i>
                                    {t('documents.upload.tabAi')}
                                </Button>
                            </div>
                        </div>

                        {activeTab === 'form' ? (
                            <UploadDocumentForm
                                values={formValues}
                                onChange={setFormValues}
                                rate={rate}
                                loading={loading}
                                uploadProgress={uploadProgress}
                                files={files}
                                setFiles={(next) => {
                                    setCloudFile(null);
                                    setFiles(next);
                                }}
                                onSubmit={handleSubmit}
                                allowMarkPaid={canMarkPaid}
                                fileLocked={Boolean(cloudFile)}
                            />
                        ) : (
                            <UploadDocumentAiPanel
                                isBusy={aiBusy}
                                handleOpenClick={handleOpenClick}
                                processingLabel={processingLabel}
                                phase={aiPhase}
                                handleCancel={handleCancel}
                                uploadProgress={aiUploadProgress}
                                refInputFile={refInputFile}
                                handleChangeFile={handleChangeFile}
                            />
                        )}
                    </div>
                ) : null}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffUploadDocument;
